'use client'

import { useState, type ReactNode } from 'react'
import Link from 'next/link'
import { IBM_Plex_Mono } from 'next/font/google'
import { HB_PLAYBOOK_BUY_PATH, HB_PLAYBOOK_PRODUCT } from '@/lib/home-builder-playbook'
import './hb-services.css'

const plexMono = IBM_Plex_Mono({
  subsets: ['latin'],
  weight: ['400', '500', '600'],
})


function Stars() {
  return (
    <div className="offer-stars" aria-label="5 out of 5 stars">
      {Array.from({ length: 5 }, (_, i) => (
        <svg key={i} width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
          <path
            fill="#e8a317"
            d="M12 2.4l2.7 6.1 6.6.6-5 4.4 1.5 6.4L12 16.7 6.2 19.9 7.7 13.5l-5-4.4 6.6-.6L12 2.4z"
          />
        </svg>
      ))}
    </div>
  )
}

function ExtraAgentIcon() {
  return (
    <svg className="play-extra-svg" viewBox="0 0 48 48" aria-hidden="true">
      <rect x="6" y="8" width="24" height="32" rx="4" fill="#e8eef6" stroke="#0a1628" strokeWidth="1.6" />
      <path d="M12 16h12M12 22h9M12 28h7" stroke="#0a1628" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="34" cy="30" r="8.5" fill="#0478FB" />
      <path d="M31 30h6M34 27v6" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" />
      <circle cx="34" cy="30" r="3" fill="none" stroke="#fff" strokeWidth="1.4" />
    </svg>
  )
}

function ExtraPacksIcon() {
  return (
    <svg className="play-extra-svg" viewBox="0 0 48 48" aria-hidden="true">
      <rect x="8" y="14" width="20" height="26" rx="3" fill="#d7e4f4" stroke="#0a1628" strokeWidth="1.5" />
      <rect x="14" y="9" width="20" height="26" rx="3" fill="#eef4fb" stroke="#0a1628" strokeWidth="1.5" />
      <rect x="20" y="5" width="20" height="26" rx="3" fill="#fff" stroke="#0a1628" strokeWidth="1.6" />
      <path d="M26 14h8M26 19h8M26 24h5" stroke="#0478FB" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}

function ExtraSupportIcon() {
  return (
    <svg className="play-extra-svg" viewBox="0 0 48 48" aria-hidden="true">
      <circle cx="24" cy="24" r="16" fill="#e8eef6" stroke="#0a1628" strokeWidth="1.6" />
      <path d="M16 28v-2a4 4 0 014-4h8a4 4 0 014 4v2" fill="none" stroke="#0a1628" strokeWidth="1.6" strokeLinejoin="round" />
      <circle cx="24" cy="18" r="3.2" fill="#0478FB" />
      <path d="M20 34h8" stroke="#0478FB" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  )
}

function ExtraCreditIcon() {
  return (
    <svg className="play-extra-svg" viewBox="0 0 48 48" aria-hidden="true">
      <rect x="8" y="12" width="32" height="24" rx="6" fill="#e8eef6" stroke="#0a1628" strokeWidth="1.6" />
      <circle cx="24" cy="24" r="7" fill="#0478FB" />
      <path d="M24 19.5v9M21.4 22.2c.7-.8 1.6-1.1 2.6-1.1 1.6 0 2.7.8 2.7 2.1 0 2.8-5.3 1.6-5.3 4.2 0 1.2 1.1 2 2.7 2 1.1 0 2-.4 2.6-1.1" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}


const PLAYBOOK_CRMS = [
  { name: 'Zoho CRM', src: '/images/logo-zoho.png' },
  { name: 'HubSpot', src: '/images/logo-hubspot.svg' },
  { name: 'Salesforce', src: '/images/logo-salesforce.svg' },
  { name: 'Buildertrend', src: '/images/home-builders/buildertrend.png' },
  { name: 'JobTread', src: '/images/logo-jobtread.png' },
] as const

type PlayItem = { t: string; sub?: string; d: string }

type PlaySection = {
  id: string
  title: string
  blurb: string
  open?: boolean
  items?: PlayItem[]
  body?: string[]
}

const SECTIONS: PlaySection[] = [
  {
    id: 'pipeline',
    title: 'Part I - The pipeline',
    blurb: 'Chapters 1-5',
    items: [
      {
        t: 'Turn a messy sales process into one pipeline',
        sub: 'Stages',
        d: 'Seven stages for custom homes, five for spec. Each stage has clear entry and exit criteria, an owner, and a target duration - so your team always knows what happens next. Includes printable stage guides and the CRM-ready stage structure.',
      },
      {
        t: 'Stop losing good leads to slow follow-up',
        sub: 'Intake & Speed to Lead',
        d: 'Respond faster without making your sales team sound automated. Acknowledge every inquiry within a minute and get a real person involved within an hour. The system prioritizes a call and text over an automated reply - and gives your team the scripts to do it consistently.',
      },
      {
        t: 'Spend sales time on leads most likely to become contracts',
        sub: 'Qualification',
        d: 'A simple scoring system ranks leads by land, timeline, and responsiveness, while budget and decision-makers are handled on the qualification call. Includes the scorecard, call guide, and disqualification templates.',
      },
      {
        t: 'Stop giving away hours of estimating',
        sub: 'Discovery, Agreement & Proposal',
        d: 'Move from discovery to concept and ballpark, then use a paid agreement before producing the real number. Present the proposal in person with a defined process for handling objections. Includes agendas, checklists, and objection cards.',
      },
      {
        t: 'Stop letting good leads disappear',
        sub: 'Follow-Up & Nurture',
        d: 'Use defined follow-up cadences for custom and spec leads, with 16-20 touches for custom and 8-10 for spec. The system adjusts timing based on lead quality instead of leaving follow-up to memory. Includes five cadences and a year of nurture emails.',
      },
    ],
  },
  {
    id: 'data',
    title: 'Part II - The data',
    blurb: 'Chapters 6-7',
    items: [
      {
        t: 'Capture only the data your sales process needs',
        sub: 'What to Capture',
        d: 'Separate the person from the opportunity and connect every CRM field to a decision in the pipeline. No bloated database and no mystery fields. Includes the complete field specification and picklist pack.',
      },
      {
        t: 'Know which marketing produces contracts',
        sub: 'Attribution',
        d: 'Capture the original source at inquiry, preserve it, and separately track what influenced the buyer later. Includes attribution rules and a consistent UTM vocabulary for web, phone, and referral leads.',
      },
    ],
  },
  {
    id: 'running',
    title: 'Part III - Running it',
    blurb: 'Chapters 8-11',
    items: [
      {
        t: 'Automate the work your team should not have to remember',
        sub: 'Automation Map',
        d: 'Twelve automations take leads through the pipeline, trigger follow-up, and keep the CRM current. Each is documented as a simple trigger, condition, and action sequence so it can be built in virtually any CRM.',
      },
      {
        t: 'Know where your pipeline is making or losing money',
        sub: 'The Numbers',
        d: 'Track eight numbers from speed-to-lead through cost per contract by source. Every metric has a defined formula and ties directly to the data captured in the pipeline. Includes the dashboard specification and weekly scorecard.',
      },
      {
        t: 'Make the sales process repeatable across your team',
        sub: 'Roles & Rhythm',
        d: 'Define five core roles and four operating rhythms - daily, weekly, monthly, and quarterly. Includes checklists, meeting agendas, and a RACI so everyone knows who owns what.',
      },
      {
        t: 'Turn a signed contract into your next lead',
        sub: 'Handoff & Referral Loop',
        d: 'Get production involved within a week of contract, ask for the review at the right moment, and create a structured 90-day referral process that keeps past clients generating opportunities.',
      },
    ],
  },
  {
    id: 'install',
    title: 'Part IV - Putting it in',
    blurb: 'Chapter 12',
    items: [
      {
        t: 'Go from playbook to working sales system in 30 days',
        sub: '30-Day Installation',
        d: 'Build the structure first, then intake and follow-up, then automation, then reporting. Every step has an owner and a deadline, with the core stages going into your CRM by day three. Includes the 30-day rollout checklist and CRM Mapping Worksheet to translate the playbook\'s fields, stages, and workflows into the CRM you already use.',
      },
    ],
  },
  {
    id: 'appendices',
    title: 'Appendices',
    blurb: '',
    items: [
      {
        t: 'Everything you need to implement it',
        sub: 'Glossary and file index',
        d: 'A builder/CRM glossary plus a complete index of the 31 included files, with chapter and page references so your team can find exactly what they need.',
      },
    ],
  },
]

type Playbook = {
  id: string
  title: string
  kicker: string
  intro: ReactNode[]
  stats: string
  image: string
  cover: string
  price: number
  productId: string
  tags: string[]
  ribbon?: string
}

const PLAYBOOKS: Playbook[] = [
  {
    id: 'inquiry-to-contract',
    title: 'Inquiry to Contract',
    kicker: 'The Home Builder Pipeline Playbook',
    intro: [
      <>A complete sales system for taking a home buyer from first inquiry to signed contract - and building the process into your CRM.</>,
      <>
        <strong>83 pages</strong> of practical guidance, <strong>31 ready-to-use templates</strong>, <strong>12 automations</strong>, <strong>8 core sales metrics</strong>, <strong>5 CRM setup packs</strong>, and a <strong>private AI agent</strong> that helps you implement the system as you go.
      </>,
      <>Designed to be installed in <strong>30 days</strong> - not just read.</>,
    ],
    stats: '83 pages. 12 chapters. 31 template files. 5 CRM packs.',
    image: '/images/home-builders/playbook.png',
    cover: '/images/home-builders/playbook_landscape.webp',
    price: HB_PLAYBOOK_PRODUCT.price,
    productId: HB_PLAYBOOK_PRODUCT.id,
    tags: ['Works with any CRM', 'AI access included', 'Install in 30 days'],
    ribbon: 'Sales System',
  },
]

function CrmRow({ className = 'crms' }: { className?: string }) {
  return (
    <div className={className}>
      {PLAYBOOK_CRMS.map(c => (
        <div key={c.name}>
          <img src={c.src} alt={c.name} />
          <span>{c.name}</span>
        </div>
      ))}
    </div>
  )
}



function ChevronIcon() {
  return (
    <svg className="play-chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
      <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function Accordion({
  id,
  title,
  blurb,
  open,
  children,
}: {
  id: string
  title: string
  blurb: string
  open?: boolean
  children: ReactNode
}) {
  const [expanded, setExpanded] = useState(!!open)
  return (
    <details
      className="play-acc"
      open={expanded}
      onToggle={e => setExpanded(e.currentTarget.open)}
    >
      <summary>
        <div>
          <h3>{title}</h3>
          {blurb ? <p>{blurb}</p> : null}
        </div>
        <ChevronIcon />
      </summary>
      <div className="play-acc-body" id={`${id}-body`}>
        {children}
      </div>
    </details>
  )
}

function PlaybookBuyButton({ productId, price, name }: { productId: string; price: number; name: string }) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function startCheckout() {
    if (loading) return
    setError(null)
    setLoading(true)

    try {
      const res = await fetch('/api/services/book', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product: productId }),
      })
      const data = await res.json()

      if (!res.ok || !data.url) {
        setError(data.error || 'Could not start checkout. Please try again.')
        setLoading(false)
        return
      }

      if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({
          event: 'ph_checkout_started',
          serviceName: name,
          serviceSlug: HB_PLAYBOOK_PRODUCT.slug,
          totalPrice: price,
        })
      }

      window.location.href = data.url
    } catch {
      setError('Network error. Please try again.')
      setLoading(false)
    }
  }

  return (
    <div>
      <button
        type="button"
        className="btn offer-buy"
        id="playbook-buy"
        onClick={startCheckout}
        disabled={loading}
      >
        {loading ? 'Redirecting…' : 'Buy & Download the Playbook'}
        <em>${price}</em>
      </button>
      {error ? <p className="offer-buy-err">{error}</p> : null}
    </div>
  )
}

function PlaybookSidebar({ playbook }: { playbook: Playbook }) {
  return (
    <div className="rt offer-side fade" key={`playbook-offer-${playbook.id}`}>
      <div className="offer-img">
        <img src={playbook.image} alt={playbook.kicker} />
      </div>
      <PlaybookBuyButton
        productId={playbook.productId}
        price={playbook.price}
        name={`${playbook.title} - ${playbook.kicker}`}
      />
      <blockquote className="offer-quote">
        <Stars />
        <p>“I&apos;d bought different CRMs twice before. This is the first time anyone told me what to put in it.”</p>
        <cite>Steve, CA homebuilder</cite>
      </blockquote>
    </div>
  )
}

function IncludedWithEveryCopy({ row = false }: { row?: boolean }) {
  return (
    <div className="play-gallery-includes">
      <h2 className="play-sec-title">Included with every playbook copy</h2>
      <div className={`play-extras${row ? ' play-extras--row' : ''}`}>
        <div className="play-extra">
          <div className="play-extra-head">
            <div className="play-extra-ico" aria-hidden>
              <ExtraAgentIcon />
            </div>
            <h3>Playbook AI Agent</h3>
          </div>
          <p>
            A private AI that knows the whole playbook. When you hit a decision mid-install, ask it instead of guessing.
          </p>
        </div>
        <div className="play-extra">
          <div className="play-extra-head">
            <div className="play-extra-ico" aria-hidden>
              <ExtraPacksIcon />
            </div>
            <h3>CRM setup packs</h3>
          </div>
          <p>
            Ready-to-load files that mirror the stages, fields, and automations in the CRM you already run. Not a generic template. One pack each for <strong>Zoho CRM</strong>, <strong>HubSpot</strong>, <strong>Salesforce</strong>, <strong>Buildertrend</strong>, and <strong>JobTread</strong>.
          </p>
        </div>
        <div className="play-extra">
          <div className="play-extra-head">
            <div className="play-extra-ico" aria-hidden>
              <ExtraSupportIcon />
            </div>
            <h3>Install support</h3>
          </div>
          <p>
            Email me through the 30-day rollout. A stage that does not fit, a field you cannot find, or which pack to start with - I answer on the playbook, not a ticket queue.
          </p>
        </div>
        <div className="play-extra">
          <div className="play-extra-head">
            <div className="play-extra-ico" aria-hidden>
              <ExtraCreditIcon />
            </div>
            <h3>$300 credit for Systems Services</h3>
          </div>
          <p>
            Applied to your first Systems project after you buy the playbook. Use it toward CRM setup, automations, attribution, or the 30-day install if you want it built for you instead of doing it in-house.
          </p>
        </div>
      </div>
    </div>
  )
}

function PlaybookDetails({ playbook, asPage }: { playbook: Playbook; asPage?: boolean }) {
  const Title = asPage ? 'h1' : 'h2'
  return (
    <div className="fade play-copy" key={`copy-${playbook.id}`}>
      <Title id="playbook-title" className="lead play-title">{playbook.title}</Title>
      <p className="play-kicker">{playbook.kicker}</p>
      {playbook.intro.map((para, i) => (
        <p key={i} className="play-intro">{para}</p>
      ))}

      {!asPage ? <IncludedWithEveryCopy /> : null}

      <h2 className="play-sec-title">What&apos;s inside</h2>
      <p className="play-stats">
        <strong>83 pages</strong>. <strong>12 chapters</strong>. <strong>31 template files</strong>. <strong>5 CRM packs</strong>.
      </p>

      <div className="play-accs">
        {SECTIONS.map(section => (
          <Accordion
            key={section.id}
            id={section.id}
            title={section.title}
            blurb={section.blurb}
          >
            {section.items?.length ? (
              <ul className="play-section-items">
                {section.items.map(item => (
                  <li key={item.t} className="play-section-item">
                    <h4>
                      {item.t}
                      {item.sub ? <em>{item.sub}</em> : null}
                    </h4>
                    <p>{item.d}</p>
                  </li>
                ))}
              </ul>
            ) : null}
            {section.body?.map(para => (
              <p key={para.slice(0, 32)} className="play-section-body">
                {para}
              </p>
            ))}
          </Accordion>
        ))}
      </div>

      <h2 className="play-sec-title">Works with CRMs</h2>
      <p className="play-crm-lead">
        Written platform-agnostic. The stages, fields, and automations are described so they can be built in any CRM with custom fields and a workflow tool - or in a spreadsheet.
      </p>
      <p className="play-crm-lead"><strong>Setup packs included for:</strong></p>
      <CrmRow className="crms play-crms" />
      <p className="play-crm-note">
        On anything else, start from the CRM Mapping Worksheet in Chapter 12. The playbook side comes pre-filled.
      </p>
    </div>
  )
}

export function PlaybookTabCard({
  selected,
  tabRef,
  onSelect,
}: {
  selected: boolean
  tabRef: (el: HTMLButtonElement | null) => void
  onSelect: () => void
}) {
  const playbook = PLAYBOOKS[0]

  return (
    <div className="play-tile">
      {playbook.ribbon ? <span className="play-tile-ribbon">{playbook.ribbon}</span> : null}
      <button
        ref={tabRef}
        type="button"
        className="c-hit play-tile-hit"
        role="tab"
        id="hb-tab-playbook"
        aria-selected={selected}
        aria-controls="hb-panel-playbook"
        aria-labelledby="hb-tab-label-playbook"
        tabIndex={selected ? 0 : -1}
        onClick={onSelect}
      >
        <div className="play-tile-img">
          <img src={playbook.cover} alt="" />
        </div>
        <div className="play-tile-copy">
          <h3 id="hb-tab-label-playbook">{playbook.title}</h3>
          <p className="play-tile-kicker">{playbook.kicker}</p>
        </div>
      </button>
      <Link href={HB_PLAYBOOK_BUY_PATH} className="c-cta play-tile-cta">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M6 6h15l-1.5 9h-12z" strokeLinejoin="round" />
          <path d="M6 6L5 3H2" strokeLinecap="round" />
          <circle cx="9" cy="20" r="1.4" />
          <circle cx="18" cy="20" r="1.4" />
        </svg>
        Buy Playbook
      </Link>
    </div>
  )
}

export function HBPlaybookProductPage() {
  const playbook = PLAYBOOKS[0]
  return (
    <div className="font-sans bg-[#f8f9fa] text-[#202124]">
      <section
        className="hb-svc play-page"
        style={{ ['--hb-mono' as string]: plexMono.style.fontFamily }}
        aria-labelledby="playbook-title"
      >
        <div className="grid-bg" />
        <div className="wrap">
          <div className="play-page-panel">
            <div className="play-page-grid">
              <PlaybookDetails playbook={playbook} asPage />
              <PlaybookSidebar playbook={playbook} />
            </div>
            <IncludedWithEveryCopy row />
          </div>
        </div>
      </section>
    </div>
  )
}

export default function HBPlaybook() {
  return (
    <div
      className="panel panel-attach-playbook"
      role="tabpanel"
      id="hb-panel-playbook"
      aria-labelledby="hb-tab-playbook"
    >
      <IncludedWithEveryCopy row />
    </div>
  )
}
